<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import { usePointerSwipe, useIntervalFn, useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import nextFrame from "../lib/utils/nextFrame.js";

const { autoplay, interval, options } = defineProps({
    autoplay: { type: Boolean, default: false },
    interval: { type: Number, default: 3000 },
    options: { type: Object, default: {} },
});

const breakpoints = useBreakpoints(breakpointsTailwind);

const currentIndex = ref(options.itemsPerView);
const isTransitioning = ref(true);
const isHovered = ref(false);
const isSwipingActive = ref(false);
const wrapperEl = ref();
const items = ref([]);
const displayItems = ref([]);

const slidesPerView = computed(() => {
    for ( const [ key, bp ] of Object.entries(options.breakpoints || {} ) ) {
        if ( breakpoints.greaterOrEqual( key ).value ) return bp.itemsPerView;
    }

    return options.itemsPerView || 3;
} );

const gapRem = computed(() => {
    for ( const [ key, bp ] of Object.entries(options.breakpoints || {} ) ) {
        if ( breakpoints.greaterOrEqual( key ).value ) return bp.gapRem;
    }

    return options.gapRem || 0.5;
} );

function initSlider() {
    const slides = slidesPerView.value;

    if (items.value.length >= slides) {
        const firstClones = items.value.slice(0, slides).map(el => el.cloneNode(true));
        const lastClones = items.value.slice(-slides).map(el => el.cloneNode(true));

        displayItems.value = [...lastClones, ...items.value, ...firstClones];
        currentIndex.value = slides;
    } else {
        displayItems.value = [...items.value];
        currentIndex.value = 0;
    }

    wrapperEl.value.replaceChildren(...displayItems.value);

    const itemWidthPercent = 100 / slidesPerView.value;

    Array.from( wrapperEl.value.children ).forEach( (child, index) => {
        child.style.flex = `0 0 calc(${itemWidthPercent}% - ${((gapRem.value) * (slidesPerView.value - 1)) / slidesPerView.value}rem)`;
        child.setAttribute('data-slide', index );
    } );
}

const wrapperStyle = computed(() => {
    const itemPercent = 100 / slidesPerView.value;
    const dragOffset = isSwiping.value ? -distanceX.value : 0;
    const gapOffset = (gapRem.value/slidesPerView.value) * currentIndex.value;

    return {
        gap: `${(gapRem.value)}rem`,
        transform: `translateX(calc(-${currentIndex.value * itemPercent}% - ${gapOffset}rem + ${dragOffset}px))`,
        transition: isSwiping.value || !isTransitioning.value ? 'none' : 'transform 0.3s ease-out'
    }
});

const isLooping = computed(() => items.value.length > slidesPerView.value);

function next() {
    if ( ! isTransitioning.value || ! isLooping.value ) return;
    currentIndex.value++;
}

function previous() {
    if ( ! isTransitioning.value || ! isLooping.value ) return;
    currentIndex.value--;
}

const { pause, resume } = useIntervalFn(
    () => {
        const slides = slidesPerView.value;

        if ( autoplay && !isHovered.value && ! isSwiping.value && displayItems.value.length > slides ) {
            next();
        }
    },
    interval,
    { immediate: autoplay }
);

const { distanceX, isSwiping, stop } = usePointerSwipe( wrapperEl, {
    threshold: 10,

    onSwipeStart() {
        if ( ! isLooping.value ) return;
        isSwipingActive.value = false;
        pause();
    },

    onSwipe() {
        if ( ! isLooping.value ) return;
        if ( Math.abs( distanceX.value ) > 10 ) {
            isSwipingActive.value = true;
        }
    },

    onSwipeEnd(e, direction) {
        if ( ! isLooping.value ) return;

        const threshold = 50;

        if ( Math.abs( distanceX.value ) > threshold ) {
            if ( direction === 'left' ) next();
            else if ( direction === 'right' ) previous();
        }

        if ( autoplay ) resume();
    }
} );

function handleTransitionEnd() {
    const slides = slidesPerView.value;
    const realCount = items.value.length;

    if (realCount < slides) return;

    if (currentIndex.value >= realCount + slides) {
        isTransitioning.value = false;
        currentIndex.value = slides;
    } else if (currentIndex.value <= slides - 1) {
        isTransitioning.value = false;
        currentIndex.value = realCount + (slides - 1);
    }

    nextFrame( () => isTransitioning.value = true );
}

function handleContainerClick(event) {
    if ( isSwipingActive.value ) {
        event.preventDefault();
        event.stopPropagation();
        isSwipingActive.value = false;
    }
}

onMounted(() => {
    items.value = Array.from( wrapperEl.value.children );

    initSlider();

    if ( autoplay ) resume();

    if ( ! isLooping.value ) stop();
});

watch(slidesPerView, () => {
    isTransitioning.value = false;
    initSlider();

    nextFrame( () => isTransitioning.value = true );
} );

const goToSlide = (slideIndex) => {
    if (slideIndex < 0 || slideIndex >= items.value.length) return;

    const targetIndex = items.value.length > slidesPerView.value
        ? slideIndex + slidesPerView.value
        : slideIndex;

    isTransitioning.value = true;
    currentIndex.value = targetIndex;
}

defineExpose({
    goToSlide,
    next,
    previous,
    currentIndex,
    isLooping,
    isSwiping,
});

</script>

<template>
    <div class="slider-container"
         @mouseenter="isHovered = true"
         @mouseleave="isHovered = false"
    >
        <div
            class="slider-wrapper | mb-8"
            :class="Object.assign({}, {
                'cursor-grab': isLooping,
                'cursor-grabbing': isSwiping && isLooping,
            }, {[options.wrapperClass || '']: true})"
            :style="wrapperStyle"
            ref="wrapperEl"
            @transitionend="handleTransitionEnd"
            @click.capture="handleContainerClick"
            @dragstart.prevent
        >
            <slot></slot>
        </div>

        <div class="flex flex-row place-content-center gap-x-2" v-if="options?.arrows === true">
            <button :disabled="! isLooping"
                    class="border border-light bg-white text-darkblue hover:border-copper transition-colors rounded-full h-12 w-12 flex flex-col justify-center place-items-center
                        disabled:opacity-50 disabled:pointer-events-none"
                    @click="previous"
                    type="button">
                <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button :disabled="! isLooping"
                    class="border border-light bg-white text-darkblue hover:border-copper transition-colors rounded-full h-12 w-12 flex flex-col justify-center place-items-center
                        disabled:opacity-50 disabled:pointer-events-none"
                    @click="next"
                    type="button">
                <i class="fa-solid fa-chevron-right"></i>
            </button>
        </div>
    </div>
</template>